import { CartAddEvent, QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

function formatMoney(cents, format) {
  const value = Number(cents || 0) / 100;
  const group = function (n, decimals, thousands, decimal) {
    const fixed = n.toFixed(decimals).split('.');
    const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fixed[1] ? whole + decimal + fixed[1] : whole;
  };
  return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
    switch (key) {
      case 'amount_no_decimals': return group(value, 0, ',', '.');
      case 'amount_with_comma_separator': return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
      case 'amount_with_space_separator': return group(value, 2, ' ', ',');
      default: return group(value, 2, ',', '.');
    }
  });
}

function productFormQuantity(scope) {
  const section = scope.closest('.shopify-section') || document;
  return section.querySelector('product-form-component quantity-selector-component') || document.querySelector('product-form-component quantity-selector-component');
}

function readTiers() {
  const offer = document.querySelector('sb-pdp-offer');
  if (!offer) return [];
  return Array.from(offer.querySelectorAll('[data-tier]')).map(function (t) { return { qty: Number(t.dataset.qty) || 1, pct: Number(t.dataset.pct) || 0 }; }).filter(function (t) { return t.pct > 0; }).sort(function (a, b) { return a.qty - b.qty; });
}

class SBPdpOffer extends HTMLElement {
  connectedCallback() {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.format = this.dataset.moneyFormat;
    this.price = Number(this.dataset.price) || 0;
    this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('keydown', this.onKey, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariant, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantity, { signal });
    this.render();
  }
  disconnectedCallback() { if (this.controller) this.controller.abort(); }
  onClick = (event) => {
    const tier = event.target.closest('[data-tier]');
    if (!tier) return;
    this.select(tier, true);
  };
  onKey = (event) => {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
    if (keys.indexOf(event.key) === -1) return;
    const index = this.tiers.indexOf(document.activeElement);
    if (index === -1) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = this.tiers[(index + step + this.tiers.length) % this.tiers.length];
    this.select(next, true);
    next.focus();
  };
  select(tier, apply) {
    this.tiers.forEach(function (item) {
      const on = item === tier;
      item.setAttribute('aria-checked', on ? 'true' : 'false');
      item.tabIndex = on ? 0 : -1;
    });
    if (apply) this.applyQuantity(Number(tier.dataset.qty) || 1);
  }
  applyQuantity(quantity) {
    const selector = productFormQuantity(this);
    if (!selector) return;
    this.syncing = true;
    if (typeof selector.setValue === 'function') {
      selector.setValue(String(quantity));
    } else {
      const input = selector.querySelector('input[name="quantity"]');
      if (input) input.value = String(quantity);
    }
    if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
    selector.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
    this.syncing = false;
  }
  onQuantity = (event) => {
    if (this.syncing) return;
    const source = event.target;
    if (!(source instanceof Element) || !source.closest('product-form-component')) return;
    const quantity = Number(event.detail && event.detail.quantity) || 1;
    let match = null;
    this.tiers.forEach(function (tier) { if ((Number(tier.dataset.qty) || 1) <= quantity) match = tier; });
    if (match) this.select(match, false);
  };
  onVariant = (event) => {
    const variant = event.detail && event.detail.resource;
    if (!variant || variant.price == null) return;
    const productId = event.detail.data && event.detail.data.productId;
    if (productId && this.dataset.productId && String(productId) !== this.dataset.productId) return;
    this.price = Number(variant.price) || this.price;
    this.render();
  };
  render() {
    const price = this.price;
    const format = this.format;
    this.tiers.forEach(function (tier) {
      const quantity = Number(tier.dataset.qty) || 1;
      const pct = Number(tier.dataset.pct) || 0;
      const full = price * quantity;
      const total = Math.round(full * (100 - pct) / 100);
      const each = Math.round(price * (100 - pct) / 100);
      const totalEl = tier.querySelector('[data-total]');
      const compareEl = tier.querySelector('[data-compare]');
      const eachEl = tier.querySelector('[data-each]');
      if (totalEl) totalEl.textContent = formatMoney(total, format);
      if (compareEl) compareEl.textContent = pct > 0 ? formatMoney(full, format) : '';
      if (eachEl) eachEl.textContent = pct > 0 ? formatMoney(each, format) + ' each, save ' + formatMoney(full - total, format) : eachEl.dataset.single || '';
    });
  }
}

class SBPdpUpsell extends HTMLElement {
  connectedCallback() {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('change', this.onChange, { signal });
    const tiers = readTiers();
    const sub = this.querySelector('[data-up-sub]');
    if (sub && tiers.length) sub.textContent = 'Add any ' + tiers[0].qty + ' pieces and save ' + tiers[0].pct + '% automatically';
  }
  disconnectedCallback() { if (this.controller) this.controller.abort(); }
  onChange = (event) => {
    const select = event.target.closest('select[data-variant]');
    if (!select) return;
    const card = select.closest('[data-card]');
    const option = select.selectedOptions[0];
    if (option && option.dataset.price) card.querySelector('[data-price]').textContent = option.dataset.price;
    card.querySelector('[data-status]').textContent = '';
    select.classList.remove('is-invalid');
  };
  message(count) {
    const tiers = readTiers();
    let current = null;
    let next = null;
    tiers.forEach(function (tier) { if (count >= tier.qty) current = tier; else if (!next) next = tier; });
    let text = 'Added to your bag.';
    if (current) text = 'Added. ' + current.pct + '% off unlocked in your bag.';
    if (next) text += ' Add ' + (next.qty - count) + ' more for ' + next.pct + '% off.';
    return text;
  }
  onClick = async (event) => {
    const button = event.target.closest('[data-add]');
    if (!button || this.busy) return;
    const card = button.closest('[data-card]');
    const select = card.querySelector('[data-variant]');
    const status = card.querySelector('[data-status]');
    const variantId = Number(select && select.value);
    if (!Number.isSafeInteger(variantId) || variantId < 1) {
      status.textContent = 'Please choose a size first.';
      if (select && select.tagName === 'SELECT') { select.classList.add('is-invalid'); select.focus(); }
      return;
    }
    this.busy = true;
    button.disabled = true;
    const label = button.textContent;
    button.textContent = 'Adding';
    status.textContent = '';
    let added = false;
    try {
      const sections = Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(function (el) { return el.dataset.sectionId; }).filter(Boolean))).slice(0, 5);
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This size could not be added. Please try another.');
      added = true;
      button.textContent = 'Added';
      const cartResponse = await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = await cartResponse.json();
      status.textContent = this.message(cart.item_count || 0);
      document.dispatchEvent(new CartAddEvent(cart, 'sb-pdp-upsell', { source: 'sb-pdp-upsell', itemCount: cart.item_count, variantId: String(variantId), sections: result.sections }));
    } catch (error) {
      status.textContent = added ? 'Added to your bag.' : (error && error.message) || 'Something went wrong. Please try again.';
    } finally {
      this.busy = false;
      setTimeout(function () { button.disabled = false; button.textContent = label; }, added ? 1600 : 0);
    }
  };
}

class SBPdpEta extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min) || 6;
    const max = Number(this.dataset.max) || min;
    const skipSaturday = this.dataset.skipSaturday === 'true';
    const add = function (start, days) {
      const date = new Date(start.getTime());
      let count = 0;
      while (count < days) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day === 0 || (skipSaturday && day === 6)) continue;
        count += 1;
      }
      return date;
    };
    const fmt = function (date) { return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); };
    const now = new Date();
    const out = this.querySelector('[data-eta]');
    if (!out) return;
    const first = add(now, min);
    const last = add(now, max);
    out.textContent = min === max ? 'by ' + fmt(first) : fmt(first) + ' and ' + fmt(last);
    const label = this.querySelector('[data-eta-label]');
    if (label && this.dataset.label) label.textContent = min === max ? 'Delivered' : this.dataset.label;
  }
}

if (!customElements.get('sb-pdp-offer')) customElements.define('sb-pdp-offer', SBPdpOffer);
if (!customElements.get('sb-pdp-upsell')) customElements.define('sb-pdp-upsell', SBPdpUpsell);
if (!customElements.get('sb-pdp-eta')) customElements.define('sb-pdp-eta', SBPdpEta);
